import { activeInstance } from '../instances'
import { productName } from '../lib/config'

/**
 * Full-page boot / navigation loader — ported from Omni-Project-Manager's
 * OmniLoadingScreen so the shell carries the same branded moment.
 *
 * Big, bold branding on a deep-ink canvas: the tenant's real mark at the
 * center of an orbital system (scanning gradient arcs + brand-colour
 * particles), the wordmark below it, and a gradient loading sweep.
 *
 * Logos are the real assets (never reconstructed) — see brand/README.md.
 * Honours prefers-reduced-motion (see @omni/ui tokens.css).
 */

const IS_EDNA_BRAND = activeInstance.slug === 'enterprise-dna'

/** Mix a hex colour toward white (amount 0..1). Non-hex accents pass through. */
function tint(hex: string, amount: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1], 16)
  const mix = (c: number) => Math.round(c + (255 - c) * amount)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(mix)
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

/** hex + alpha → rgba() string. Non-hex accents fall back to the hex itself. */
function alpha(hex: string, a: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

/** Mix a hex colour toward black (amount 0..1). */
function shade(hex: string, amount: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1], 16)
  const mix = (c: number) => Math.round(c * (1 - amount))
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(mix)
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

// The EDNA splash keeps its exact amber/pink/purple system; every other
// instance derives the orbital palette from its own accent so the boot
// moment reads as THAT brand, not Omni's (EDNA-3017).
const BRAND = IS_EDNA_BRAND
  ? {
      ink: '#0b0c18',
      amber: '#f2b347',
      pink: '#ca5a8b',
      purple: '#6654f5',
    }
  : {
      ink: '#0b0c18',
      amber: tint(activeInstance.theme.accent, 0.35),
      pink: activeInstance.theme.accent,
      purple: shade(activeInstance.theme.accent, 0.3),
    }

// Particles ride invisible rings of different radii, each on its own orbit
// container so they rotate independently.
const PARTICLES = [
  { radius: 150, duration: 8, reverse: false, color: BRAND.amber, size: 7, delay: 0 },
  { radius: 150, duration: 8, reverse: false, color: BRAND.purple, size: 5, delay: -4 },
  { radius: 108, duration: 5.5, reverse: true, color: BRAND.pink, size: 9, delay: -1.4 },
  { radius: 192, duration: 12, reverse: false, color: BRAND.pink, size: 5, delay: -6 },
  { radius: 192, duration: 12, reverse: true, color: BRAND.amber, size: 4, delay: -2.5 },
]

const IS_EDNA = activeInstance.slug === 'enterprise-dna'

export function Splash({ label }: { label: string }) {
  const logoUrl = activeInstance.theme.logoUrl

  return (
    <div
      className="fixed inset-0 z-[100] flex w-full flex-col items-center justify-center overflow-hidden"
      style={{ background: BRAND.ink }}
    >
      {/* Ambient aurora + vignette */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          className="animate-omni-float absolute -top-48 -left-40 h-[680px] w-[680px] rounded-full blur-3xl"
          style={{ background: alpha(BRAND.amber, 0.18) }}
        />
        <div
          className="animate-omni-float absolute top-1/4 -right-48 h-[760px] w-[760px] rounded-full blur-3xl"
          style={{ background: alpha(BRAND.pink, 0.2), animationDelay: '-5s' }}
        />
        <div
          className="animate-omni-float absolute -bottom-56 left-1/4 h-[720px] w-[720px] rounded-full blur-3xl"
          style={{ background: alpha(BRAND.purple, 0.22), animationDelay: '-10s' }}
        />
        {/* Faint dot grid for a technical feel */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)',
            backgroundSize: '34px 34px',
          }}
        />
        {/* Vignette to sink the edges into ink */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 42%, transparent 0%, transparent 38%, rgba(11,12,24,0.85) 80%)',
          }}
        />
      </div>

      {/* Orbital core */}
      <div aria-hidden className="relative h-[400px] w-[400px]">
        {/* Outer scan arc */}
        <div className="animate-omni-spin-12 absolute inset-0">
          <div
            className="omni-arc h-full w-full"
            style={{
              background: `conic-gradient(from 90deg, transparent 0deg, transparent 200deg, ${BRAND.purple}00 230deg, ${BRAND.purple} 320deg, ${BRAND.pink} 360deg)`,
            }}
          />
        </div>

        {/* Outer hairline ring */}
        <div
          className="animate-omni-ring-breathe absolute inset-0 rounded-full border"
          style={{ borderColor: alpha(BRAND.purple, 0.22) }}
        />

        {/* Middle scan arc, reversed */}
        <div className="animate-omni-spin-rev-16 absolute inset-[52px]">
          <div
            className="omni-arc omni-arc-thick h-full w-full"
            style={{
              background: `conic-gradient(from 0deg, transparent 0deg, transparent 240deg, ${BRAND.amber}00 270deg, ${BRAND.amber} 340deg, ${BRAND.pink} 360deg)`,
            }}
          />
        </div>

        {/* Inner hairline ring */}
        <div
          className="animate-omni-ring-breathe absolute inset-[104px] rounded-full border"
          style={{ borderColor: alpha(BRAND.pink, 0.22), animationDelay: '-1.5s' }}
        />

        {/* Orbiting particles */}
        {PARTICLES.map((p, i) => (
          <div
            key={i}
            className={`absolute left-1/2 top-1/2 ${p.reverse ? 'animate-omni-spin-rev-6' : 'animate-omni-spin-8'}`}
            style={{ animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s` }}
          >
            <span
              className="animate-omni-particle absolute rounded-full"
              style={{
                width: p.size,
                height: p.size,
                background: p.color,
                left: p.radius,
                top: -p.size / 2,
                marginLeft: -p.size / 2,
                boxShadow: `0 0 14px ${p.color}, 0 0 5px ${p.color}`,
                animationDelay: `${p.delay}s`,
              }}
            />
          </div>
        ))}

        {/* Center tenant mark with breathing glow */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="relative">
            <span
              aria-hidden
              className="animate-omni-core-glow absolute left-1/2 top-1/2 -z-10 h-[230px] w-[230px] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                background: `radial-gradient(circle, ${alpha(BRAND.purple, 0.55)} 0%, ${alpha(BRAND.pink, 0.3)} 42%, transparent 70%)`,
                filter: 'blur(14px)',
              }}
            />
            {logoUrl ? (
              // max-h/max-w with w-auto keeps wide wordmarks (e.g. TMM's
              // 4:1 white mark) at their real aspect instead of squashing
              // them into a 120px square.
              <img
                src={logoUrl}
                alt={activeInstance.displayName}
                className="relative max-h-[120px] w-auto max-w-[300px] object-contain"
                style={{ filter: `drop-shadow(0 8px 40px ${alpha(BRAND.purple, 0.45)})` }}
              />
            ) : (
              <span
                className="relative flex h-[120px] w-[120px] items-center justify-center rounded-3xl text-5xl font-bold text-white"
                style={{ background: 'var(--gradient-brand)', filter: `drop-shadow(0 8px 40px ${alpha(BRAND.purple, 0.45)})` }}
              >
                {(activeInstance.displayName || 'O').charAt(0)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Big bold wordmark */}
      <div className="relative -mt-2 flex flex-col items-center">
        {IS_EDNA ? (
          <img
            src="/branding/edna-wordmark-white.png"
            alt="Enterprise DNA"
            className="h-9 w-auto md:h-11"
          />
        ) : (
          <span className="text-2xl font-semibold text-white md:text-3xl">
            {activeInstance.displayName}
          </span>
        )}
        <span className="mt-4 text-[11px] font-semibold uppercase tracking-[0.52em] text-white/40">
          {!__FRONT_DOOR__ && IS_EDNA ? 'Command Centre' : productName}
        </span>

        {/* Status line */}
        <div className="mt-7 h-5 overflow-hidden">
          <p key={label} className="animate-fade-in-up text-sm font-medium text-white/55">
            {label.replace(/…\s*$/, '')}
            <span className="animate-pulse-dot ml-1" style={{ color: BRAND.pink }}>…</span>
          </p>
        </div>

        {/* Gradient progress track with a sweeping highlight */}
        <div
          className="relative mt-4 h-[2px] w-64 overflow-hidden rounded-full"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <div
            className="animate-omni-progress absolute top-0 h-full w-1/3 rounded-full"
            style={{
              background: `linear-gradient(90deg, transparent, ${BRAND.amber}, ${BRAND.pink}, ${BRAND.purple}, transparent)`,
            }}
          />
        </div>
      </div>
    </div>
  )
}
